/**
 * CRM-grounded multi-format content generation — the agentic core of the
 * Content Engine (directly answers auditor findings #1 and #11: "generation
 * is not agentic", "multi-format is a dropdown").
 *
 * PURE by contract except `generateAsset`, whose only side effect is one
 * call through the REAL worker seam (`lib/agent/argoWorker`) when a worker
 * is injected. Everything else — format specs, voice profiles, prompt
 * assembly, the deterministic fallback, and validation — is deterministic:
 * same inputs, same strings, same `promptHash`.
 *
 * The grounding pipeline:
 *
 *   brandDoc + companyBrain ──▶ buildVoiceProfile ─┐
 *   persona / account / merge fields / win themes ─┼─▶ buildPrompt ─▶ worker
 *   FORMAT_SPECS (true per-format constraints) ────┘        │
 *                                                           ▼ (absent/failed)
 *                                                  deterministicFallback
 *
 * The worker path uses `argoWorker.runAgent` — the exact seam every other
 * agentic feature uses — so when `VITE_AGENT_API_URL` is set the drafts are
 * real Claude output grounded in live CRM facts. When it is not, the
 * deterministic fallback produces a format-shaped, voice-aware draft from
 * the same context, so the feature is never dead and tests are stable.
 * Provenance (`usedWorker`, `model`, `promptHash`) is always reported so
 * the UI can say honestly which path produced the text.
 *
 * No customer data is ever fabricated: every fact in a prompt or fallback
 * draft comes from the caller-supplied context (resolved merge fields,
 * persona vocabulary, win themes, brand voice) — unknowns are omitted.
 */

import * as argoWorker from '../agent/argoWorker';
import type { PersonaProfile } from '../db/types';

// ---------------------------------------------------------------------------
// Format specs — true per-format constraints (not a dropdown of labels)
// ---------------------------------------------------------------------------

/** One output format's hard constraints, embedded in prompts + validation. */
export interface FormatSpec {
  /** Stable id — the record key in FORMAT_SPECS. */
  format: string;
  /**
   * Hard character limit. For `xThread` this is PER POST (the platform
   * limit); for everything else it applies to the whole asset.
   */
  charLimit?: number;
  /** Required structural parts, in order. */
  structure: string[];
  /** A clear call to action must be present. */
  ctaRequired: boolean;
  /** The first line must be a hook — no warm-up openers. */
  hookFirst: boolean;
}

export const FORMAT_SPECS: Record<string, FormatSpec> = {
  blogPost: {
    format: 'blogPost',
    structure: ['headline', 'intro', 'sections', 'takeaway', 'cta'],
    ctaRequired: true,
    hookFirst: true,
  },
  linkedinPost: {
    format: 'linkedinPost',
    charLimit: 3000,
    structure: ['hook', 'story', 'proof', 'cta'],
    ctaRequired: true,
    hookFirst: true,
  },
  xThread: {
    format: 'xThread',
    charLimit: 280, // per post
    structure: ['hook post', 'body posts', 'cta post'],
    ctaRequired: true,
    hookFirst: true,
  },
  coldEmail: {
    format: 'coldEmail',
    charLimit: 1500,
    structure: ['subject', 'preheader', 'opener', 'body', 'cta', 'signature'],
    ctaRequired: true,
    hookFirst: true,
  },
  adCopy: {
    format: 'adCopy',
    charLimit: 125,
    structure: ['headline', 'proof', 'cta'],
    ctaRequired: true,
    hookFirst: true,
  },
  landingHero: {
    format: 'landingHero',
    charLimit: 300,
    structure: ['headline', 'subheadline', 'cta'],
    ctaRequired: true,
    hookFirst: true,
  },
};

/** Human tab labels for the view (FormatSpec itself stays constraint-only). */
export const FORMAT_LABELS: Record<string, string> = {
  blogPost: 'Blog post',
  linkedinPost: 'LinkedIn post',
  xThread: 'X thread',
  coldEmail: 'Cold email',
  adCopy: 'Ad copy',
  landingHero: 'Landing hero',
};

// ---------------------------------------------------------------------------
// Voice profile — from the brand doc + canonical Company Brain facts
// ---------------------------------------------------------------------------

/** The voice every generated asset must be written in. */
export interface VoiceProfile {
  name: string;
  toneRules: string[];
  bannedPhrases: string[];
  examples: string[];
}

/** Loose brand-doc shape: structured voice fields, or a markdown body. */
export interface BrandDocLike {
  title?: string;
  entityName?: string;
  /** Markdown body — "Banned phrases: a, b" lines are parsed out of it. */
  body?: string;
  voiceName?: string;
  toneRules?: string[];
  bannedPhrases?: string[];
  examples?: string[];
}

interface BrainFactLike {
  section?: string;
  field?: string;
  label?: string;
  value?: string;
}

/** Loose Company Brain shape: a flat fact list, or a BrainView's sections. */
export interface CompanyBrainLike {
  facts?: BrainFactLike[];
  sections?: Array<{ meta?: { id?: string }; facts?: BrainFactLike[] }>;
}

/** House defaults — used when no brand doc supplies its own. */
const DEFAULT_TONE_RULES = [
  'Plain, direct, first-person — write like a founder, not a marketer',
  'Concrete numbers over adjectives',
  'Short sentences; no filler, no throat-clearing',
];

const DEFAULT_BANNED = [
  'leverage',
  'synergy',
  'game-changer',
  'seamless',
  'unlock',
  'revolutionary',
  'cutting-edge',
];

/** Pull "Banned phrases: a, b" lines out of a markdown brand-doc body. */
function parseBannedFromBody(body: string | undefined): string[] {
  if (!body) return [];
  const out: string[] = [];
  const re = /banned[^:\n]*:\s*([^\n]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    for (const piece of m[1].split(',')) {
      const p = piece.trim().replace(/\.$/, '');
      if (p) out.push(p);
    }
  }
  return out;
}

function dedupe(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const key = item.toLowerCase();
    if (item.trim() && !seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

function brainFacts(brain: CompanyBrainLike | null | undefined): BrainFactLike[] {
  if (!brain) return [];
  if (brain.facts) return brain.facts;
  if (brain.sections) return brain.sections.flatMap((s) => s.facts ?? []);
  return [];
}

/**
 * Build the voice every asset is written in: brand-doc tone rules, banned
 * phrases (structured + parsed from the body + house buzzword bans), and
 * examples, folded together with the Company Brain's canonical claims
 * (`positioning` facts become voice examples) and operator guardrails
 * (`rules` facts become tone rules). Deterministic; unknowns are omitted.
 */
export function buildVoiceProfile(
  brandDoc?: BrandDocLike | null,
  companyBrain?: CompanyBrainLike | null,
): VoiceProfile {
  const facts = brainFacts(companyBrain);
  const ruleFacts = facts.filter((f) => f.section === 'rules' && f.value);
  const positioningFacts = facts.filter((f) => f.section === 'positioning' && f.value);

  const toneRules = dedupe([
    ...(brandDoc?.toneRules?.length ? brandDoc.toneRules : DEFAULT_TONE_RULES),
    ...ruleFacts.map((f) => (f.label ? `${f.label}: ${f.value}` : String(f.value))),
  ]);

  const bannedPhrases = dedupe([
    ...(brandDoc?.bannedPhrases ?? []),
    ...parseBannedFromBody(brandDoc?.body),
    ...DEFAULT_BANNED,
  ]);

  const examples = dedupe([
    ...(brandDoc?.examples ?? []),
    ...positioningFacts.map((f) => String(f.value)),
  ]);

  return {
    name:
      brandDoc?.voiceName?.trim() ||
      brandDoc?.title?.trim() ||
      brandDoc?.entityName?.trim() ||
      'Argo house voice',
    toneRules,
    bannedPhrases,
    examples,
  };
}

// ---------------------------------------------------------------------------
// Generation context
// ---------------------------------------------------------------------------

/** Everything a generation run is grounded in. Only supplied facts are used. */
export interface GenContext {
  account?: { name?: string; ownerName?: string } | null;
  person?: { name?: string; title?: string; company?: string } | null;
  persona?: {
    archetype?: string;
    summary?: string;
    profile?: PersonaProfile | null;
  } | null;
  voice: VoiceProfile;
  /** Already-resolved merge fields (token → VALUE), e.g. from resolveMerge. */
  mergeFields: Record<string, string>;
  /** Why we win, from the win-loss rollup (lib/crm/winloss). */
  winThemes?: string[];
  /** 0–100 customer health (lib/crm/health), when targeting a customer. */
  healthScore?: number;
}

// ---------------------------------------------------------------------------
// Prompt assembly
// ---------------------------------------------------------------------------

function bullet(lines: string[]): string {
  return lines.map((l) => `- ${l}`).join('\n');
}

/**
 * Build the exact system + user prompt for one brief × format × context.
 * Deterministic: same inputs, same strings (merge fields are emitted in
 * sorted key order). The system prompt carries the voice (tone rules,
 * banned-phrase directive, examples), persona vocabulary, resolved CRM
 * facts, win themes, health, and every format constraint; the user prompt
 * carries the brief and the content-only output discipline.
 */
export function buildPrompt(
  brief: string,
  spec: FormatSpec,
  ctx: GenContext,
): { system: string; user: string } {
  const parts: string[] = [];
  const v = ctx.voice;

  parts.push(
    `You are the ArgoIQ Content Engine writing a ${spec.format} in the "${v.name}" voice.`,
  );

  if (v.toneRules.length > 0) {
    parts.push('', 'Tone rules (follow every one):', bullet(v.toneRules));
  }
  if (v.bannedPhrases.length > 0) {
    parts.push(
      '',
      `Never use these banned phrases: ${v.bannedPhrases.map((p) => `"${p}"`).join(', ')}.`,
    );
  }
  if (v.examples.length > 0) {
    parts.push('', 'Voice examples (match this register):', bullet(v.examples));
  }

  const persona = ctx.persona;
  if (persona) {
    const vocab: string[] = [];
    const prof = persona.profile ?? undefined;
    if (persona.archetype) vocab.push(`Archetype: ${persona.archetype}`);
    if (prof?.role) vocab.push(`Role: ${prof.role}`);
    if (prof?.painPoints?.length) vocab.push(`Pains: ${prof.painPoints.join('; ')}`);
    if (prof?.goals?.length) vocab.push(`Goals: ${prof.goals.join('; ')}`);
    if (prof?.kpis?.length) vocab.push(`KPIs they are measured on: ${prof.kpis.join('; ')}`);
    if (prof?.objections?.length) vocab.push(`Objections to pre-empt: ${prof.objections.join('; ')}`);
    if (prof?.quote) vocab.push(`In their words: "${prof.quote}"`);
    if (persona.summary) vocab.push(`Summary: ${persona.summary}`);
    if (vocab.length > 0) {
      parts.push('', 'Audience persona — use their vocabulary:', bullet(vocab));
    }
  }

  const mergeEntries = Object.entries(ctx.mergeFields).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  if (mergeEntries.length > 0) {
    parts.push(
      '',
      'CRM facts (ground the copy in these VALUES; never invent others):',
      bullet(mergeEntries.map(([k, val]) => `${k}: ${val}`)),
    );
  }

  if (ctx.winThemes?.length) {
    parts.push('', `Themes that closed our recent wins: ${ctx.winThemes.join('; ')}.`);
  }
  if (typeof ctx.healthScore === 'number') {
    parts.push('', `Customer health score: ${ctx.healthScore}/100 — calibrate the tone accordingly.`);
  }

  const constraints: string[] = [`Structure, in order: ${spec.structure.join(' → ')}`];
  if (spec.charLimit !== undefined) {
    constraints.push(
      spec.format === 'xThread'
        ? `Hard limit: ${spec.charLimit} characters PER POST (separate posts with a blank line)`
        : `Hard limit: ${spec.charLimit} characters total`,
    );
  }
  if (spec.ctaRequired) constraints.push('A clear call to action is required');
  if (spec.hookFirst) constraints.push('Open with a hook — no warm-up lines, no "I hope this finds you well"');
  parts.push('', `Format constraints for ${spec.format}:`, bullet(constraints));

  const user = [
    `Brief: ${brief.trim()}`,
    '',
    'Respond with only the finished content — no preamble, no explanation, no quotes around it.',
  ].join('\n');

  return { system: parts.join('\n'), user };
}

// ---------------------------------------------------------------------------
// Prompt hash — stable provenance for identical inputs
// ---------------------------------------------------------------------------

/** FNV-1a 32-bit hex — tiny, deterministic, dependency-free. */
function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------
// Deterministic fallback — format-shaped, voice-aware, never dead
// ---------------------------------------------------------------------------

/** Clamp to a hard char limit, ending on an ellipsis when cut. */
function clamp(text: string, limit: number): string {
  if (text.length <= limit) return text;
  return text.slice(0, Math.max(0, limit - 1)).trimEnd() + '…';
}

/** First sentence-cased line from the brief, period-free tail. */
function hookLine(brief: string): string {
  const t = brief.trim().replace(/\.+$/, '');
  return t.length > 0 ? t.charAt(0).toUpperCase() + t.slice(1) : 'A result worth reading';
}

interface Grounding {
  company?: string;
  contact?: string;
  owner?: string;
  pain?: string;
  goal?: string;
  theme?: string;
  proof?: string;
}

function ground(ctx: GenContext): Grounding {
  return {
    company: ctx.account?.name?.trim() || ctx.mergeFields['account.name'],
    contact: ctx.person?.name?.trim() || ctx.mergeFields['contact.name'],
    owner: ctx.account?.ownerName?.trim() || ctx.mergeFields['owner.name'],
    pain: ctx.persona?.profile?.painPoints?.[0],
    goal: ctx.persona?.profile?.goals?.[0],
    theme: ctx.winThemes?.[0],
    proof: ctx.voice.examples[0],
  };
}

/**
 * Deterministic, offline draft honoring the format's structure and char
 * limits. Grounded ONLY in supplied context (account name, persona pains,
 * win themes, voice examples) — unknowns are simply omitted, never
 * invented. This is what ships when the worker is absent or fails, and
 * every format's output passes `validateAsset` against its own spec.
 */
export function deterministicFallback(
  brief: string,
  spec: FormatSpec,
  ctx: GenContext,
): string {
  const g = ground(ctx);
  const hook = hookLine(brief);
  const who = g.company ? `teams like ${g.company}` : 'teams like yours';
  const painLine = g.pain
    ? `The pattern we keep hearing from ${who}: ${g.pain}.`
    : `We built this after hearing the same problem from ${who}.`;
  const proofLine = g.proof
    ? `Proof, not promises: ${g.proof}`
    : g.theme
      ? `The deals we win close on one thing: ${g.theme.toLowerCase()}.`
      : 'The numbers came from real accounts, not projections.';
  const goalLine = g.goal ? `If the goal this quarter is to ${lcFirst(g.goal)}, this is the shortest path.` : '';

  switch (spec.format) {
    case 'blogPost': {
      return [
        `# ${hook}`,
        '',
        painLine,
        '',
        '## What actually happened',
        proofLine,
        '',
        '## The takeaway',
        goalLine || 'The fastest wins came from starting small and measuring weekly.',
        '',
        `Want the same numbers? Reply and we'll start a scoped pilot.`,
      ].join('\n');
    }
    case 'linkedinPost': {
      const text = [
        `${hook}.`,
        '',
        painLine,
        '',
        proofLine,
        ...(goalLine ? ['', goalLine] : []),
        '',
        `If that sounds like your quarter, reply "playbook" and I'll send the breakdown.`,
      ].join('\n');
      return spec.charLimit ? clamp(text, spec.charLimit) : text;
    }
    case 'xThread': {
      const limit = spec.charLimit ?? 280;
      const posts = [
        `1/ ${hook}.`,
        `2/ ${painLine}`,
        `3/ ${proofLine}`,
        `4/ ${goalLine || 'Small scoped start, weekly measurement, compounding wins.'} Reply and I'll send the playbook.`,
      ];
      return posts.map((p) => clamp(p, limit)).join('\n\n');
    }
    case 'coldEmail': {
      const text = [
        `Subject: ${clamp(hook, 70)}`,
        `Preheader: ${clamp(proofLine, 90)}`,
        '',
        `Hi ${g.contact ?? 'there'},`,
        '',
        `${painLine} ${proofLine}`,
        ...(goalLine ? ['', goalLine] : []),
        '',
        `Worth 15 minutes? Reply here and I'll send times.`,
        '',
        `— ${g.owner ?? 'The Argo team'}`,
      ].join('\n');
      return spec.charLimit ? clamp(text, spec.charLimit) : text;
    }
    case 'adCopy': {
      const suffix = ' See how — start a pilot.';
      const limit = spec.charLimit ?? 125;
      return `${clamp(`${hook}.`, Math.max(1, limit - suffix.length))}${suffix}`;
    }
    case 'landingHero': {
      const limit = spec.charLimit ?? 300;
      const headline = clamp(hook, 80);
      const sub = clamp(
        g.pain
          ? `Built for ${g.company ?? 'teams'} tired of ${lcFirst(g.pain)}. ${g.proof ?? ''}`.trim()
          : proofLine,
        Math.max(20, limit - headline.length - 20),
      );
      return [headline, sub, 'Start a pilot →'].join('\n');
    }
    default: {
      // Unknown spec shape: still produce a hook + CTA draft.
      return [`${hook}.`, '', painLine, '', `Reply and we'll start a pilot.`].join('\n');
    }
  }
}

function lcFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Validation — enforce the spec on any draft (worker or fallback)
// ---------------------------------------------------------------------------

const CTA_RE =
  /\b(reply|book|schedule|call|visit|try|start|sign up|learn more|see how|download|join|register|subscribe|let'?s talk|grab (a )?time|get started|reach out|dm me|worth a look)\b/i;

const WEAK_OPENER_RE =
  /^(i hope|hope (you|this)|just (checking|following|touching|reaching)|i wanted to|i am (excited|thrilled)|i'm (excited|thrilled)|we are (excited|pleased|proud)|we're (excited|pleased|proud)|in today's|in a world|as you (may )?know|greetings|dear sir)/i;

/** Strip structural prefixes ("1/ ", "Subject:", "# ") before hook checks. */
function firstContentLine(text: string): string {
  const line = text.split('\n').find((l) => l.trim().length > 0) ?? '';
  return line
    .trim()
    .replace(/^\d+\/\s*/, '')
    .replace(/^(subject|headline|preheader):\s*/i, '')
    .replace(/^#+\s*/, '')
    .trim();
}

/**
 * Enforce a format spec on a draft: character limits (per post for
 * xThread), CTA presence, and hook-first opening. Every violation names
 * the specific rule broken so the UI (and the operator) can fix it.
 */
export function validateAsset(
  text: string,
  spec: FormatSpec,
): { ok: boolean; violations: string[] } {
  const violations: string[] = [];
  const body = text ?? '';

  if (body.trim().length === 0) {
    return { ok: false, violations: ['empty draft'] };
  }

  if (spec.charLimit !== undefined) {
    if (spec.format === 'xThread') {
      const posts = body.split(/\n\n+/).filter((p) => p.trim().length > 0);
      posts.forEach((post, i) => {
        if (post.length > spec.charLimit!) {
          violations.push(
            `post ${i + 1} is ${post.length} chars — exceeds the ${spec.charLimit}-char per-post limit`,
          );
        }
      });
    } else if (body.length > spec.charLimit) {
      violations.push(
        `draft is ${body.length} chars — exceeds the ${spec.charLimit}-char limit for ${spec.format}`,
      );
    }
  }

  if (spec.ctaRequired && !CTA_RE.test(body)) {
    violations.push(`missing a call to action (required for ${spec.format})`);
  }

  if (spec.hookFirst && WEAK_OPENER_RE.test(firstContentLine(body))) {
    violations.push('weak warm-up opener — lead with a hook (a number, a scene, or a claim)');
  }

  return { ok: violations.length === 0, violations };
}

// ---------------------------------------------------------------------------
// generateAsset — the one async entry point
// ---------------------------------------------------------------------------

/** The worker seam: exactly the real module's surface, injectable for tests. */
export type ContentWorker = Pick<typeof argoWorker, 'runAgent' | 'isConfigured'>;

export interface GeneratedAsset {
  text: string;
  format: string;
  provenance: {
    /** Which engine produced the text. */
    model: string;
    /** True only when the REAL worker returned this text. */
    usedWorker: boolean;
    /** Stable hash of the exact prompt — identical inputs, identical hash. */
    promptHash: string;
  };
}

/**
 * Generate one asset for a brief × format × grounded context.
 *
 * When `deps.worker` is present AND configured, the exact grounded prompt
 * is dispatched through the real `argoWorker.runAgent` seam (AWS → Claude)
 * and the worker's text is returned verbatim with `usedWorker: true`.
 * When the worker is absent, unconfigured, errors, or returns nothing,
 * the deterministic fallback answers instead — same prompt hash, honest
 * `usedWorker: false` — so the feature degrades gracefully, never dead.
 *
 * @throws Error on an unknown format id — that is a programming error,
 *         not a runtime condition to paper over.
 */
export async function generateAsset(
  brief: string,
  format: string,
  ctx: GenContext,
  deps: { worker?: ContentWorker } = {},
): Promise<GeneratedAsset> {
  const spec = FORMAT_SPECS[format];
  if (!spec) {
    throw new Error(`Unknown content format: "${format}"`);
  }

  const prompt = buildPrompt(brief, spec, ctx);
  const promptHash = fnv1a(`${spec.format}\u0000${prompt.system}\u0000${prompt.user}`);

  const worker = deps.worker;
  if (worker && worker.isConfigured()) {
    try {
      const out = await worker.runAgent(
        {
          id: `content-engine-${spec.format}`,
          name: 'Content Engine',
          template: 'content-writer',
          persona: `${ctx.voice.name} — ${spec.format} writer`,
        },
        {
          system: prompt.system,
          user: prompt.user,
          prompt: `${prompt.system}\n\n${prompt.user}`,
        },
      );
      const text = out.text.trim();
      if (text.length > 0) {
        return {
          text,
          format: spec.format,
          provenance: { model: 'argo-agent-worker', usedWorker: true, promptHash },
        };
      }
      // Empty reply → fall through to the deterministic path below.
    } catch {
      // Worker unreachable/failed → graceful deterministic fallback.
    }
  }

  return {
    text: deterministicFallback(brief, spec, ctx),
    format: spec.format,
    provenance: { model: 'deterministic-fallback', usedWorker: false, promptHash },
  };
}
